import React, { useEffect, useMemo, useState } from 'react';
import { Zap, Check, X } from 'react-feather';
import api from './api';

const PRODUCE_OPTIONS = [
    { id: 'script', label: 'Scripts only' },
    { id: 'script_voiceover', label: 'Scripts + voiceover' },
    { id: 'script_voiceover_video', label: 'Scripts + voiceover + video' },
];

const PUBLISH_OPTIONS = [
    { id: 'auto_publish', label: 'Auto-schedule & publish' },
    { id: 'auto_draft', label: 'Auto-schedule as draft' },
    { id: 'require_approval', label: 'Require approval' },
];

const AFTER_APPROVAL_OPTIONS = [
    { id: 'auto_publish', label: 'Then auto-schedule & publish' },
    { id: 'auto_draft', label: 'Then auto-schedule as draft' },
    { id: 'let_user_schedule', label: 'Then let me schedule' },
];

function defaultsFromWorkspace(workspace) {
    const d = workspace?.dailyAutomation || {};
    return {
        enabled: !!d.enabled,
        dailyCount: Math.max(1, Math.min(10, Number(d.dailyCount) || 3)),
        produce: d.produce || 'script',
        durationSec: [15, 30, 60].includes(Number(d.durationSec)) ? Number(d.durationSec) : 30,
        topic: d.topic || '',
        publishMode: d.publishMode || 'require_approval',
        afterApproval: d.afterApproval || 'let_user_schedule',
        platforms: {
            instagram: d.platforms?.instagram !== false,
            tiktok: d.platforms?.tiktok !== false,
        },
        scheduleTime: /^\d{2}:\d{2}$/.test(d.scheduleTime || '') ? d.scheduleTime : '10:00',
        spacingHours: Math.max(1, Math.min(24, Number(d.spacingHours) || 2)),
    };
}

function ChoiceRow({ options, value, onChange, disabled }) {
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {options.map((opt) => (
                <button
                    key={opt.id}
                    type="button"
                    disabled={disabled}
                    className={`home-choice-btn ${value === opt.id ? 'home-choice-btn--on' : 'home-choice-btn--off'}`}
                    onClick={() => onChange(opt.id)}
                    style={{ opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

export default function WorkspaceHomeAutomation({
    currentWorkspace,
    updateWorkspace,
    getlateConnectedAccounts = { instagram: false, tiktok: false },
    onOpenProject,
}) {
    const workspaceId = currentWorkspace?._id;
    const [form, setForm] = useState(() => defaultsFromWorkspace(currentWorkspace));
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [items, setItems] = useState([]);
    const [busyItemId, setBusyItemId] = useState(null);
    const [scheduleDraft, setScheduleDraft] = useState({});
    const [message, setMessage] = useState('');

    useEffect(() => {
        setForm(defaultsFromWorkspace(currentWorkspace));
    }, [currentWorkspace?._id, currentWorkspace?.dailyAutomation]);

    const loadItems = async () => {
        if (!workspaceId) return;
        try {
            const res = await api.getWorkspaceAutomationItems(workspaceId);
            setItems(res.data?.items || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => {
        loadItems();
    }, [workspaceId]);

    const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));
    const connected = getlateConnectedAccounts.instagram || getlateConnectedAccounts.tiktok;
    const publishEnabled = form.produce === 'script_voiceover_video';

    const summary = useMemo(() => {
        const produceLabel = PRODUCE_OPTIONS.find((o) => o.id === form.produce)?.label || form.produce;
        if (!form.enabled) return 'Automation is off';
        return `Every day: ${form.dailyCount} × ${produceLabel.toLowerCase()}`;
    }, [form.enabled, form.dailyCount, form.produce]);

    const lastRunLabel = currentWorkspace?.dailyAutomation?.lastRunStatus
        ? `Last run: ${currentWorkspace.dailyAutomation.lastRunStatus}${currentWorkspace.dailyAutomation.lastRunDateKey ? ` (${currentWorkspace.dailyAutomation.lastRunDateKey})` : ''}`
        : '';

    const payload = () => ({
        dailyAutomation: {
            ...form,
            timezone: 'UTC',
        },
    });

    const save = async () => {
        if (!workspaceId || !updateWorkspace) return;
        setSaving(true);
        setMessage('');
        try {
            await updateWorkspace(workspaceId, payload(), { skipLoading: true });
            setMessage('Saved');
        } catch (err) {
            setMessage(err.response?.data?.error || err.message || 'Failed to save');
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        if (!workspaceId) return;
        setRunning(true);
        setMessage('Saving & starting today’s run…');
        try {
            await updateWorkspace(workspaceId, payload(), { skipLoading: true });
            await api.runWorkspaceAutomation(workspaceId);
            setMessage('Run started. New projects appear here as they finish.');
            setTimeout(loadItems, 8000);
            setTimeout(loadItems, 30000);
        } catch (err) {
            setMessage(err.response?.data?.error || err.message || 'Failed to start');
        } finally {
            setRunning(false);
        }
    };

    const pending = items.filter((it) => (
        it.status === 'pending_approval'
        || it.status === 'approved'
        || it.status === 'producing'
        || it.status === 'failed'
    ));

    return (
        <section className="home-automation-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
                    <Zap size={16} />
                    Daily automation
                </h4>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={form.enabled}
                        onChange={(e) => patch({ enabled: e.target.checked })}
                        style={{ accentColor: 'var(--button-bg)' }}
                    />
                    {form.enabled ? 'On' : 'Off'}
                </label>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-secondary)' }}>{summary}</p>
            {lastRunLabel && (
                <p style={{ margin: '-8px 0 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {lastRunLabel}
                    {currentWorkspace?.dailyAutomation?.lastRunError ? ` — ${currentWorkspace.dailyAutomation.lastRunError}` : ''}
                </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>How many per day?</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                            type="button"
                            className="home-choice-btn home-choice-btn--off"
                            disabled={form.dailyCount <= 1}
                            onClick={() => patch({ dailyCount: form.dailyCount - 1 })}
                            style={{ width: 26, height: 26, padding: 0 }}
                        >
                            −
                        </button>
                        <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 600 }}>{form.dailyCount}</span>
                        <button
                            type="button"
                            className="home-choice-btn home-choice-btn--off"
                            disabled={form.dailyCount >= 10}
                            onClick={() => patch({ dailyCount: form.dailyCount + 1 })}
                            style={{ width: 26, height: 26, padding: 0 }}
                        >
                            +
                        </button>
                    </span>
                    <span style={{ width: 1, height: 22, background: 'var(--input-border)', opacity: 0.85 }} aria-hidden />
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Duration</span>
                    {[15, 30, 60].map((sec) => (
                        <button
                            key={sec}
                            type="button"
                            className={`home-choice-btn ${form.durationSec === sec ? 'home-choice-btn--on' : 'home-choice-btn--off'}`}
                            onClick={() => patch({ durationSec: sec })}
                        >
                            {sec}s
                        </button>
                    ))}
                </div>

                <div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>Produce</div>
                    <ChoiceRow options={PRODUCE_OPTIONS} value={form.produce} onChange={(produce) => patch({ produce })} />
                </div>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Topic (optional)</span>
                    <textarea
                        value={form.topic}
                        onChange={(e) => patch({ topic: e.target.value })}
                        rows={2}
                        placeholder="What should these scripts focus on?"
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: '1px solid var(--input-border)',
                            background: 'var(--input-bg)',
                            color: 'var(--text-color)',
                            fontSize: 13,
                            resize: 'vertical',
                        }}
                    />
                </label>

                <div style={{ opacity: publishEnabled ? 1 : 0.55 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Publishing {publishEnabled ? '' : '(needs full videos)'}
                    </div>
                    <ChoiceRow
                        options={PUBLISH_OPTIONS}
                        value={form.publishMode}
                        onChange={(publishMode) => patch({ publishMode })}
                        disabled={!publishEnabled}
                    />
                    {publishEnabled && (
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                            Publish and draft go through connected GetLate accounts. Approval can then auto-publish, send as draft, or leave scheduling to you.
                        </p>
                    )}
                    {form.publishMode === 'require_approval' && publishEnabled && (
                        <div style={{ marginTop: 8 }}>
                            <ChoiceRow
                                options={AFTER_APPROVAL_OPTIONS}
                                value={form.afterApproval}
                                onChange={(afterApproval) => patch({ afterApproval })}
                            />
                        </div>
                    )}
                    {(form.publishMode === 'auto_publish' || form.publishMode === 'auto_draft' || form.afterApproval !== 'let_user_schedule') && publishEnabled && (
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                First post (UTC)
                                <input
                                    type="time"
                                    value={form.scheduleTime}
                                    onChange={(e) => patch({ scheduleTime: e.target.value })}
                                    style={{
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                    }}
                                />
                            </label>
                            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                Spacing (hours)
                                <input
                                    type="number"
                                    min={1}
                                    max={24}
                                    value={form.spacingHours}
                                    onChange={(e) => patch({ spacingHours: Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1)) })}
                                    style={{
                                        width: 64,
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                    }}
                                />
                            </label>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: getlateConnectedAccounts.instagram ? 1 : 0.5 }}>
                            <input
                                type="checkbox"
                                checked={form.platforms.instagram}
                                disabled={!getlateConnectedAccounts.instagram}
                                onChange={(e) => patch({ platforms: { ...form.platforms, instagram: e.target.checked } })}
                            />
                            Instagram {getlateConnectedAccounts.instagram ? '' : '(connect first)'}
                        </label>
                        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: getlateConnectedAccounts.tiktok ? 1 : 0.5 }}>
                            <input
                                type="checkbox"
                                checked={form.platforms.tiktok}
                                disabled={!getlateConnectedAccounts.tiktok}
                                onChange={(e) => patch({ platforms: { ...form.platforms, tiktok: e.target.checked } })}
                            />
                            TikTok {getlateConnectedAccounts.tiktok ? '' : '(connect first)'}
                        </label>
                    </div>
                    {publishEnabled && !connected && (form.publishMode !== 'require_approval' || form.afterApproval !== 'let_user_schedule') && (
                        <p style={{ fontSize: 12, color: '#e53935', margin: '8px 0 0' }}>
                            Connect Instagram or TikTok to auto-publish or send drafts.
                        </p>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        type="button"
                        className="main-button"
                        onClick={() => save().catch(() => {})}
                        disabled={saving}
                        style={{ padding: '8px 14px' }}
                    >
                        {saving ? 'Saving…' : 'Save automation'}
                    </button>
                    <button
                        type="button"
                        className="tertiary-button"
                        onClick={runNow}
                        disabled={running || !form.enabled}
                        style={{ padding: '8px 14px' }}
                    >
                        {running ? 'Starting…' : 'Run today'}
                    </button>
                    {message && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{message}</span>}
                </div>
            </div>

            {pending.length > 0 && (
                <div style={{ marginTop: 18 }}>
                    <h5 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)' }}>Needs attention</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {pending.map((item) => {
                            const script = item.scriptId && typeof item.scriptId === 'object' ? item.scriptId : null;
                            const preview = (script?.content || item.caption || '').trim();
                            const draft = scheduleDraft[item._id] || {};
                            return (
                                <div
                                    key={item._id}
                                    style={{
                                        border: '1px solid var(--input-border)',
                                        borderRadius: 8,
                                        padding: 12,
                                        background: 'var(--card-bg, var(--input-bg))',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                                            {String(item.status).replace(/_/g, ' ')}
                                        </span>
                                        {item.runDateKey && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.runDateKey}</span>}
                                    </div>
                                    <p style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.4 }}>
                                        {preview ? `${preview.slice(0, 180)}${preview.length > 180 ? '…' : ''}` : 'No script text yet'}
                                    </p>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {script?._id && (
                                            <button
                                                type="button"
                                                className="tertiary-button"
                                                onClick={() => onOpenProject?.(script._id, script)}
                                                style={{ padding: '6px 10px', fontSize: 12 }}
                                            >
                                                Open in Studio
                                            </button>
                                        )}
                                        {item.status === 'pending_approval' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="main-button"
                                                    disabled={busyItemId === item._id}
                                                    onClick={async () => {
                                                        setBusyItemId(item._id);
                                                        try {
                                                            await api.approveWorkspaceAutomationItem(workspaceId, item._id);
                                                            await loadItems();
                                                        } finally {
                                                            setBusyItemId(null);
                                                        }
                                                    }}
                                                    style={{ padding: '6px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                >
                                                    <Check size={12} /> Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className="tertiary-button"
                                                    disabled={busyItemId === item._id}
                                                    onClick={async () => {
                                                        setBusyItemId(item._id);
                                                        try {
                                                            await api.rejectWorkspaceAutomationItem(workspaceId, item._id);
                                                            await loadItems();
                                                        } finally {
                                                            setBusyItemId(null);
                                                        }
                                                    }}
                                                    style={{ padding: '6px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                >
                                                    <X size={12} /> Reject
                                                </button>
                                            </>
                                        )}
                                        {item.status === 'approved' && (
                                            <>
                                                <input
                                                    type="datetime-local"
                                                    value={draft.at || ''}
                                                    onChange={(e) => setScheduleDraft((prev) => ({ ...prev, [item._id]: { ...prev[item._id], at: e.target.value } }))}
                                                    style={{
                                                        padding: '6px 8px',
                                                        borderRadius: 6,
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        fontSize: 12,
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="main-button"
                                                    disabled={busyItemId === item._id || !draft.at}
                                                    onClick={async () => {
                                                        setBusyItemId(item._id);
                                                        try {
                                                            await api.scheduleWorkspaceAutomationItem(workspaceId, item._id, {
                                                                scheduleAt: new Date(draft.at).toISOString(),
                                                                isDraft: false,
                                                            });
                                                            await loadItems();
                                                        } finally {
                                                            setBusyItemId(null);
                                                        }
                                                    }}
                                                    style={{ padding: '6px 10px', fontSize: 12 }}
                                                >
                                                    Schedule & publish
                                                </button>
                                                <button
                                                    type="button"
                                                    className="tertiary-button"
                                                    disabled={busyItemId === item._id}
                                                    onClick={async () => {
                                                        setBusyItemId(item._id);
                                                        try {
                                                            await api.scheduleWorkspaceAutomationItem(workspaceId, item._id, { isDraft: true });
                                                            await loadItems();
                                                        } finally {
                                                            setBusyItemId(null);
                                                        }
                                                    }}
                                                    style={{ padding: '6px 10px', fontSize: 12 }}
                                                >
                                                    Send as draft
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {item.error && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#e53935' }}>{item.error}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
}
